import { MeiliSearch } from "meilisearch";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import {
  transformCreatorForSearch,
  transformCampaignForSearch,
} from "../utils/data-transforms";
import type { Doc } from "@/convex/_generated/dataModel";

// Define a proper interface for search client
interface SearchClient {
  search: (
    requests: Array<{ indexName: string; params?: Record<string, unknown> }>
  ) => Promise<{
    results: Array<{
      hits: any[];
      nbHits: number;
      page: number;
      nbPages: number;
      hitsPerPage: number;
      exhaustiveNbHits: boolean;
      query: string;
      params: string;
      processingTimeMS: number;
      facets: Record<string, any>;
      index: string;
      queryID: string;
    }>;
  }>;
  searchForFacetValues: () => Promise<{
    facetHits: any[];
    exhaustiveFacetsCount: boolean;
    processingTimeMS: number;
  }>;
}

// Environment variables with defaults for local development
const MEILISEARCH_URL =
  process.env.NEXT_PUBLIC_MEILISEARCH_URL ||
  process.env.MEILISEARCH_HOST ||
  "https://ms-667c5986d053-24239.lon.meilisearch.io";
const MEILISEARCH_API_KEY =
  process.env.NEXT_PUBLIC_MEILISEARCH_API_KEY ||
  process.env.MEILISEARCH_API_KEY ||
  "bc883cff38e61fe7c5356c8992c7954ba983e216339988926cc0633286935cfa";

// Check if environment variables are properly set
// For development, we'll allow the defaults
const isConfigured = Boolean(MEILISEARCH_URL && MEILISEARCH_API_KEY);

// console.log("Meilisearch configuration check:", {
//   url: MEILISEARCH_URL,
//   hasApiKey: Boolean(MEILISEARCH_API_KEY),
//   isConfigured,
//   envUrl: process.env.NEXT_PUBLIC_MEILISEARCH_URL,
//   envKey: process.env.NEXT_PUBLIC_MEILISEARCH_API_KEY ? "SET" : "NOT SET",
// });

// Create a function to get the search client
export function getInstantSearchClient() {
  // Only create client on the client side
  if (typeof window === "undefined") {
    console.log("Server side - creating minimal search client");
    return {
      search: async () => ({
        results: [
          {
            hits: [],
            nbHits: 0,
            page: 0,
            nbPages: 0,
            hitsPerPage: 20,
            exhaustiveNbHits: true,
            query: "",
            params: "",
            processingTimeMS: 0,
            facets: {},
            index: "creators",
            queryID: "",
          },
        ],
      }),
      searchForFacetValues: async () => ({
        facetHits: [],
        exhaustiveFacetsCount: true,
        processingTimeMS: 0,
      }),
    };
  }

  // console.log("Client side - creating Meilisearch client with:", {
  //   url: MEILISEARCH_URL,
  //   hasApiKey: Boolean(MEILISEARCH_API_KEY),
  //   envUrl: process.env.NEXT_PUBLIC_MEILISEARCH_URL,
  //   envKey: process.env.NEXT_PUBLIC_MEILISEARCH_API_KEY ? "SET" : "NOT SET",
  // });

  try {
    // Create the real Meilisearch client
    const client = instantMeiliSearch(MEILISEARCH_URL, MEILISEARCH_API_KEY, {
      placeholderSearch: true,
      primaryKey: "_id",
      keepZeroFacets: false,
    });

    // Return the nested searchClient which has the required methods
    const searchClient = (client as any).searchClient;
    if (searchClient && typeof searchClient.search === "function") {
      return searchClient;
    }

    // Fallback to whole client
    console.log("⚠️ Using whole client");
    return client;
  } catch (error) {
    console.error("❌ Failed to create Meilisearch client:", error);

    // Return a working mock client
    return {
      search: async (
        requests: Array<{ indexName: string; params?: Record<string, unknown> }>
      ) => {
        console.log("🔧 Mock search called:", requests);
        return {
          results: requests.map((request) => ({
            hits: [],
            nbHits: 0,
            page: (request.params?.page as number) || 0,
            nbPages: 0,
            hitsPerPage: (request.params?.hitsPerPage as number) || 20,
            exhaustiveNbHits: true,
            query: (request.params?.query as string) || "",
            params: "",
            processingTimeMS: 0,
            facets: {} as Record<string, any>,
            index: request.indexName || "creators",
            queryID: "",
          })),
        };
      },
      searchForFacetValues: async () => ({
        facetHits: [] as any[],
        exhaustiveFacetsCount: true,
        processingTimeMS: 0,
      }),
    };
  }
}

// Initialize standard Meilisearch client for backend operations
export const meilisearchClient = new MeiliSearch({
  host: MEILISEARCH_URL,
  apiKey: MEILISEARCH_API_KEY,
});

// Constants
export const INDEXES = {
  CREATORS: "creators",
  CAMPAIGNS: "campaigns",
} as const;

// Export for other files
export { transformCreatorForSearch, transformCampaignForSearch };

// Types for search
interface SearchQuery {
  indexName: string;
  query: string;
  options?: SearchOptions;
}

interface SearchOptions {
  filter?: string | string[];
  sort?: string[];
  limit?: number;
  offset?: number;
  attributesToSearchOn?: string[];
  attributesToRetrieve?: string[];
  attributesToHighlight?: string[];
  attributesToCrop?: string[];
  cropLength?: number;
  highlightPreTag?: string;
  highlightPostTag?: string;
  showMatchesPosition?: boolean;
  facets?: string[];
  placeholderSearch?: boolean;
  matchingStrategy?: "all" | "last";
}

// Index configuration
const CREATORS_INDEX_CONFIG = {
  primaryKey: "_id",
  searchableAttributes: [
    "name",
    "description_short",
    "description_long",
    "category",
    "_searchableText.countries",
    "_searchableText.languages",
    "_searchableText.location",
  ],
  filterableAttributes: [
    "visibility",
    "category",
    "country",
    "language",
    "avg_price",
    "socialStats",
    "socialStats.youtube",
    "socialStats.instagram",
    "socialStats.twitter",
    "socialStats.facebook",
    "socialStats.tiktok",
    "socialStats.twitch",
  ],
  sortableAttributes: [
    "name",
    "socialStats.youtube",
    "socialStats.instagram",
    "socialStats.twitter",
    "socialStats.facebook",
    "socialStats.tiktok",
    "socialStats.twitch",
  ],
  rankingRules: [
    "words",
    "typo",
    "proximity",
    "attribute",
    "sort",
    "exactness",
  ],
};

const CAMPAIGNS_INDEX_CONFIG = {
  primaryKey: "_id",
  searchableAttributes: [
    "name",
    "descriptionShort",
    "descriptionFull",
    "brandName",
    "category",
    "targetAudience",
    "campaignGoals",
    "contentGuidelines",
    "_searchableText.countries",
    "_searchableText.languages",
    "_searchableText.platforms",
    "_searchableText.hashtags",
    "_searchableText.keyMessages",
  ],
  filterableAttributes: [
    "status",
    "category",
    "country",
    "language",
    "budget",
    "socialPlatforms",
    "brandId",
    "createdAt",
    "startDate",
    "endDate",
    "applicationDeadline",
  ],
  sortableAttributes: [
    "name",
    "budget",
    "createdAt",
    "startDate",
    "endDate",
    "applicationDeadline",
  ],
  rankingRules: [
    "words",
    "typo",
    "proximity",
    "attribute",
    "sort",
    "exactness",
  ],
};

/**
 * Initialize Meilisearch indexes with basic configuration
 */
export const initializeIndexes = async () => {
  try {
    // Initialize creators index
    const creatorsIndex = meilisearchClient.index(INDEXES.CREATORS);
    await creatorsIndex.updateSettings(CREATORS_INDEX_CONFIG);

    // Initialize campaigns index
    const campaignsIndex = meilisearchClient.index(INDEXES.CAMPAIGNS);
    await campaignsIndex.updateSettings(CAMPAIGNS_INDEX_CONFIG);

    console.log("Meilisearch indexes initialized successfully");
  } catch (error) {
    console.error("Failed to initialize Meilisearch indexes:", error);
    throw error;
  }
};

/**
 * Add or update a creator document in Meilisearch
 * @param creator - Creator document from the database
 */
export const indexCreator = async (creator: Doc<"creators">) => {
  try {
    const transformedCreator = transformCreatorForSearch(creator);
    await meilisearchClient
      .index(INDEXES.CREATORS)
      .addDocuments([transformedCreator]);
  } catch (error) {
    console.error("Failed to index creator:", error);
    throw error;
  }
};

/**
 * Remove a creator document from Meilisearch
 * @param creatorId - ID of the creator to remove
 */
export const removeCreator = async (creatorId: string) => {
  try {
    await meilisearchClient.index(INDEXES.CREATORS).deleteDocument(creatorId);
  } catch (error) {
    console.error("Failed to remove creator:", error);
    throw error;
  }
};

/**
 * Search creators with Meilisearch
 * @param query - Search query
 * @param options - Search options
 */
export const searchCreators = async (
  query: string,
  options: SearchOptions = {}
) => {
  try {
    const searchResponse = await meilisearchClient
      .index(INDEXES.CREATORS)
      .search(query, {
        ...options,
        attributesToRetrieve: ["*"],
        attributesToHighlight: ["*"],
      });
    return searchResponse;
  } catch (error) {
    console.error("Failed to search creators:", error);
    throw error;
  }
};

/**
 * Add or update a campaign document in Meilisearch
 * @param campaign - Campaign document from the database
 */
export const indexCampaign = async (campaign: Doc<"campaigns">) => {
  try {
    const transformedCampaign = transformCampaignForSearch(campaign);
    await meilisearchClient
      .index(INDEXES.CAMPAIGNS)
      .addDocuments([transformedCampaign]);
  } catch (error) {
    console.error("Failed to index campaign:", error);
    throw error;
  }
};

/**
 * Remove a campaign document from Meilisearch
 * @param campaignId - ID of the campaign to remove
 */
export const removeCampaign = async (campaignId: string) => {
  try {
    await meilisearchClient.index(INDEXES.CAMPAIGNS).deleteDocument(campaignId);
  } catch (error) {
    console.error("Failed to remove campaign:", error);
    throw error;
  }
};

/**
 * Search campaigns with Meilisearch
 * @param query - Search query
 * @param options - Search options
 */
export const searchCampaigns = async (
  query: string,
  options: SearchOptions = {}
) => {
  try {
    const searchResponse = await meilisearchClient
      .index(INDEXES.CAMPAIGNS)
      .search(query, {
        ...options,
        attributesToRetrieve: ["*"],
        attributesToHighlight: ["*"],
      });
    return searchResponse;
  } catch (error) {
    console.error("Failed to search campaigns:", error);
    throw error;
  }
};

/**
 * Get a campaign by ID from Meilisearch
 * @param campaignId - ID of the campaign to retrieve
 */
export const getCampaignById = async (campaignId: string) => {
  try {
    const campaign = await meilisearchClient
      .index(INDEXES.CAMPAIGNS)
      .getDocument(campaignId);
    return campaign;
  } catch (error) {
    console.error("Failed to get campaign by ID:", error);
    throw error;
  }
};
